<template>
  <section
    v-if="isActive"
    class="flex flex-col gap-3 rounded-[10px] border-2 border-primary bg-white p-4"
    :data-testid="testId"
    :aria-busy="busy ? 'true' : 'false'"
  >
    <template v-if="isFinished">
      <h2 class="m-0 text-base font-bold text-textPrimary">Fila concluída</h2>
      <p class="m-0 text-sm text-neutral50" :data-testid="`${testId}-summary`">
        {{ doneCount }} tratados · {{ skippedCount }} pulados<template v-if="takenCount > 0">
          · {{ takenCount }} com outra pessoa</template
        >. Os pulados continuam na lista.
      </p>
      <!-- Aviso que chegou junto com o fim da fila (ex.: 409 no último item). -->
      <slot name="summary" />
      <div>
        <button
          type="button"
          class="rounded-md border border-primary bg-primary px-3 py-1.5 text-sm font-medium text-white"
          :data-testid="`${testId}-finish`"
          @click="emit('finish')"
        >
          Voltar para a lista
        </button>
      </div>
    </template>

    <template v-else>
      <div class="flex flex-wrap items-center gap-2">
        <h2 class="m-0 text-base font-bold text-textPrimary">Ataque em sequência</h2>
        <span class="text-xs text-neutral50" :data-testid="`${testId}-progress-label`">
          {{ position }} de {{ total }}
        </span>
        <slot name="clock" />
        <span class="flex-1" />
        <button
          type="button"
          class="rounded-md border border-lightGray30 px-3 py-1.5 text-sm font-medium text-textPrimary"
          :data-testid="`${testId}-quit`"
          @click="emit('quit')"
        >
          Sair
        </button>
      </div>

      <div
        class="h-1.5 overflow-hidden rounded-[3px] bg-lightGray30"
        role="progressbar"
        aria-label="Progresso da fila"
        :aria-valuenow="progressAria.now"
        :aria-valuemin="progressAria.min"
        :aria-valuemax="progressAria.max"
        :aria-valuetext="`${position} de ${total}`"
      >
        <i
          class="block h-full bg-primary transition-[width] duration-200 motion-reduce:transition-none"
          :style="{ width: progressWidth }"
          :data-testid="`${testId}-progress-bar`"
        />
      </div>

      <div class="flex flex-wrap items-center gap-2">
        <span class="text-lg font-bold text-textPrimary">{{ title }}</span>
        <span v-if="subtitle" class="font-mono text-xs text-neutral50">{{ subtitle }}</span>
        <slot name="badges" />
      </div>

      <slot name="facts" />
      <slot name="message" />

      <div class="flex flex-wrap items-center gap-2">
        <slot name="actions" />
        <span class="flex-1" />
        <button
          type="button"
          class="rounded-md border border-lightGray30 px-3 py-1.5 text-sm font-medium text-textPrimary disabled:cursor-not-allowed disabled:opacity-50"
          :data-testid="`${testId}-skip`"
          :disabled="busy"
          @click="emit('skip')"
        >
          Pular <kbd class="rounded border border-lightGray30 px-1 font-mono text-[11px]">S</kbd>
        </button>
        <button
          type="button"
          class="rounded-md border border-primary bg-primary px-3 py-1.5 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
          :data-testid="`${testId}-done`"
          :disabled="busy"
          @click="emit('done')"
        >
          Tratado, próximo
          <kbd class="rounded border border-white/40 px-1 font-mono text-[11px]">Enter</kbd>
        </button>
      </div>

      <p
        v-if="busy && hasIgnoredShortcut"
        class="m-0 text-xs text-neutral50"
        role="status"
        :data-testid="`${testId}-busy`"
      >
        Aguarde: o plantão ainda está sendo aberto. Tente de novo em instantes.
      </p>

      <p class="m-0 text-xs text-neutral50" :data-testid="`${testId}-next`">
        {{ nextLabel ? `A seguir: ${nextLabel}` : 'Este é o último da fila.' }}
      </p>
    </template>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'

const props = withDefaults(
  defineProps<{
    isActive: boolean
    isFinished: boolean
    /** 1-based. */
    position: number
    total: number
    title: string
    subtitle?: string
    /** `null` quando o item corrente é o último. */
    nextLabel: string | null
    doneCount?: number
    skippedCount?: number
    /** Itens que outra pessoa já tratava (conflito na marca): fora de "pulados". */
    takenCount?: number
    testId?: string
    /**
     * `true` enquanto a ação do item corrente está em voo (o pai liga no
     * `isBusy` do `useAttackQueue`): desabilita "Tratado, próximo" e "Pular" e
     * suspende os atalhos, para um Enter repetido não pular itens.
     */
    busy?: boolean
  }>(),
  {
    subtitle: '',
    doneCount: 0,
    skippedCount: 0,
    takenCount: 0,
    testId: 'queue-attack-panel',
    busy: false
  }
)

const emit = defineEmits<{ done: []; skip: []; quit: []; finish: [] }>()

const progressWidth = computed(() => {
  if (props.total <= 0) return '0%'
  return `${Math.round(((props.position - 1) / props.total) * 100)}%`
})

/** Com fila vazia (`total = 0`) min/max/now colapsam em 0, sem valor fora da faixa. */
const progressAria = computed(() => {
  const min = props.total > 0 ? 1 : 0
  const max = Math.max(props.total, min)
  return { min, max, now: Math.min(Math.max(props.position, min), max) }
})

/** Elementos em que Enter/S têm significado próprio e o atalho não pode roubar. */
const IGNORED_FOCUS_SELECTOR =
  'input, select, textarea, button, a[href], [contenteditable]:not([contenteditable="false"]), [role="button"]'

/**
 * O evento não é um atalho do painel: veio com modificador (Ctrl/Cmd+S é o
 * salvar do navegador), é tecla segurada, composição de IME, já foi tratado,
 * ou o foco está num elemento em que Enter/S têm significado próprio.
 */
function isForeignKeystroke(event: KeyboardEvent): boolean {
  if (event.ctrlKey || event.metaKey || event.altKey) return true
  if (event.isComposing || event.repeat || event.defaultPrevented) return true
  const target = event.target as HTMLElement | null
  return Boolean(target?.matches?.(IGNORED_FOCUS_SELECTOR))
}

/**
 * `Enter`/`S` são atalhos GLOBAIS, porque quem ataca a fila trabalha com as
 * mãos no teclado e o foco normalmente está no corpo da página. Digitar numa
 * observação ou apertar um botão nunca pode virar "tratado, próximo" — daí o
 * corte por tipo de elemento em foco, igual ao do mockup.
 */
/**
 * UX-02: um atalho durante a ação em voo continua IGNORADO (enfileirar faria um
 * Enter repetido tratar o item seguinte sem a pessoa vê-lo), mas não em
 * silêncio: o painel diz para aguardar até a ação terminar.
 */
const hasIgnoredShortcut = ref(false)
watch(
  () => props.busy,
  (busy) => {
    if (!busy) hasIgnoredShortcut.value = false
  }
)

const isShortcutKey = (event: KeyboardEvent): boolean =>
  event.key === 'Enter' || event.key?.toLowerCase() === 's'

function onKeydown(event: KeyboardEvent): void {
  if (!props.isActive || props.isFinished) return
  if (isForeignKeystroke(event)) return
  if (props.busy) {
    if (isShortcutKey(event)) {
      event.preventDefault()
      hasIgnoredShortcut.value = true
    }
    return
  }

  if (event.key === 'Enter') {
    event.preventDefault()
    emit('done')
    return
  }
  if (event.key?.toLowerCase() === 's') {
    event.preventDefault()
    emit('skip')
  }
}

onMounted(() => document.addEventListener('keydown', onKeydown))
onUnmounted(() => document.removeEventListener('keydown', onKeydown))
</script>
